// „Gültig ab“ of a VISA card (not its Apple Pay devices): the month close (Abgleich) expects a statement for the card
// only from this month up to its expiry month. New cards start in the month they are entered; an earlier issued card
// gets its month here. Opened by right-click on a card in Firma → Karten (`cardProvider`), mounted once in App.tsx.
// PATCH core/cards/:id { validFrom: "YYYY-MM" | null }.
import React from 'react';
import { api, errorText } from '../../api/client';
import { level, session } from '../../api/auth';
import { live, loadCore, type ApiCard } from '../../data/compta';
import { expiryLabel } from '../../data/cards';
import { tr2 } from '../../i18n/lang';
import type { CtxEntry } from '@rimocore/shared/ctx/menu';
import type { CtxProvider } from '@rimocore/shared/ctx/registry';

/** "2026-10-01" → "10/2026" (the app's month field). */
export const startLabel = (iso?: string | null) => (iso && /^\d{4}-\d{2}/.test(iso) ? iso.slice(5, 7) + '/' + iso.slice(0, 4) : '');
/** "10/2026" → "2026-10"; "" → null (no start); anything else → undefined (incomplete). */
export function startMonth(v: string): string | null | undefined {
  const s = v.trim(); if (!s) return null;
  const m = /^(0[1-9]|1[0-2])\/(20\d{2})$/.exec(s);
  return m ? `${m[2]}-${m[1]}` : undefined;
}

export const CARD_START_EVENT = 'rimon:card-start';
export const openCardStart = (id: string) => window.dispatchEvent(new CustomEvent(CARD_START_EVENT, { detail: { id } }));

const cardName = (k: ApiCard) => `VISA ${k.bank} ••${k.last4}`;

/** Right-click on a card (`data-ctx="card"`): „Gültig ab …“ with the current month as hint. */
export const cardProvider: CtxProvider = (ev) => {
  const el = ev.target.closest<HTMLElement>('[data-ctx="card"]');
  const k = el && live.cards.find((c) => c.id === el.dataset.ctxId);
  if (!k) return null;
  const from = startLabel(k.validFrom), edit = level(session.user, 'f_cards') === 'EDIT';
  const entries: CtxEntry[] = [
    { header: cardName(k) },
    { label: tr2('Gültig ab …', 'Valable à partir de …'), icon: 'card', hint: from || tr2('ohne', 'sans'), disabled: !edit, run: () => openCardStart(k.id) },
  ];
  return entries;
};

interface State { id: string | null; v: string; err: string; busy: boolean }

export class CardStartHost extends React.Component<object, State> {
  state: State = { id: null, v: '', err: '', busy: false };
  private open = (e: Event) => {
    const id = (e as CustomEvent<{ id: string }>).detail.id, k = live.cards.find((c) => c.id === id);
    if (k) this.setState({ id, v: startLabel(k.validFrom), err: '', busy: false });
  };
  private key = (e: KeyboardEvent) => { if (this.state.id && e.key === 'Escape' && !document.querySelector('[data-ctx-menu]')) this.close(); };
  componentDidMount() { window.addEventListener(CARD_START_EVENT, this.open); window.addEventListener('keydown', this.key); }
  componentWillUnmount() { window.removeEventListener(CARD_START_EVENT, this.open); window.removeEventListener('keydown', this.key); }
  close = () => { if (!this.state.busy) this.setState({ id: null }); };

  async save(value: string) {
    const id = this.state.id, m = startMonth(value);
    if (!id) return;
    if (m === undefined) { this.setState({ err: tr2('Monat als MM/JJJJ eintragen.', 'Saisissez le mois au format MM/AAAA.') }); return; }
    this.setState({ busy: true, err: '' });
    try {
      await api('/core/cards/' + id, { method: 'PATCH', body: { validFrom: m } });
      await loadCore();
      window.dispatchEvent(new CustomEvent('rimon:toast', { detail: { text: tr2('Gültig ab gespeichert.', 'Date de début enregistrée.'), kind: 'success' } }));
      this.setState({ id: null, busy: false });
    } catch (e) { this.setState({ busy: false, err: errorText(e) }); }
  }

  render() {
    const S = this.state, k = S.id ? live.cards.find((c) => c.id === S.id) : null;
    if (!k) return null;
    const exp = expiryLabel(k.expiresAt);
    const btn = (primary: boolean): React.CSSProperties => ({ height: 34, padding: '0 14px', borderRadius: 4, border: primary ? 'none' : '1px solid rgba(255,255,255,.16)', background: primary ? '#1375BD' : 'transparent',
      color: '#FBFAFA', font: '600 12.5px/1 Inter,sans-serif', cursor: S.busy ? 'default' : 'pointer', opacity: S.busy ? 0.6 : 1, whiteSpace: 'nowrap' });
    return (
      <div onMouseDown={(e) => e.target === e.currentTarget && this.close()} style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(10,8,9,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div role="dialog" aria-label={tr2('Gültig ab', 'Valable à partir de')} style={{ width: 400, maxWidth: '100%', borderRadius: 10, background: '#2A2627', border: '1px solid rgba(255,255,255,.1)', boxShadow: '0 24px 60px rgba(0,0,0,.5)', color: '#FBFAFA' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '18px 22px 10px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ font: '700 10px/1 Barlow,sans-serif', letterSpacing: '.14em', color: '#9B9698', textTransform: 'uppercase' }}>{tr2('Karte · Gültig ab', 'Carte · valable à partir de')}</div>
              <div data-no-tr="1" style={{ font: '800 22px/1.1 Barlow,sans-serif', marginTop: 6 }}>{cardName(k)}</div>
            </div>
            <div onClick={this.close} title={tr2('Schließen', 'Fermer')} style={{ width: 30, height: 30, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9B9698', cursor: 'pointer', font: '400 18px/1 Inter,sans-serif' }}>✕</div>
          </div>
          <div style={{ padding: '0 22px 12px', font: '400 13px/1.5 Inter,sans-serif', color: '#CDD3D9' }}>
            {tr2('Unter Abschluss wird ein Kartenauszug erst ab diesem Monat erwartet', 'Sous Clôture, un relevé de carte n’est attendu qu’à partir de ce mois')}{exp ? tr2(` · bis zum Ablauf ${exp}.`, ` · jusqu’à l’expiration ${exp}.`) : '.'}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); this.save(S.v); }} style={{ padding: '0 22px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input autoFocus value={S.v} onChange={(e) => this.setState({ v: e.target.value, err: '' })} placeholder="MM/JJJJ" data-date="month"
              style={{ height: 38, borderRadius: 4, border: '1px solid ' + (S.err ? '#E57373' : 'rgba(255,255,255,.16)'), background: 'rgba(255,255,255,.04)', color: '#FBFAFA', padding: '0 10px', font: "500 14px/1 'JetBrains Mono',monospace", outline: 'none' }} />
            {S.err && <div style={{ font: '500 12px/1.4 Inter,sans-serif', color: '#E57373' }}>{S.err}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
              <div onClick={() => !S.busy && this.save('')} title={tr2('Karte in allen Monaten erwarten', 'Attendre la carte dans tous les mois')} style={{ ...btn(false), marginRight: 'auto', display: 'flex', alignItems: 'center' }}>{tr2('Ohne Beginn', 'Sans début')}</div>
              <div onClick={this.close} style={{ ...btn(false), display: 'flex', alignItems: 'center' }}>{tr2('Abbrechen', 'Annuler')}</div>
              <button type="submit" disabled={S.busy} style={btn(true)}>{tr2('Speichern', 'Enregistrer')}</button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}
